import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  TabsProps as ChakraTabsProps
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { Tabs } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import Footer from '../../../../../components/Footer';
import ManageTagCategories from './ManageTagCategories';
import ManageTags from './ManageTags';

export const TAG_TABS = {
  USER_TAGS: 'User Tags',
  SUBMISSION_TAGS: 'Submission Tags',
  TAG_CATEGORIES: 'Tag Categories'
} as const;

export type TagTabs = (typeof TAG_TABS)[keyof typeof TAG_TABS];

type RenderTabContent = {
  activeTab: TagTabs;
};

export const renderTabContent = ({ activeTab }: RenderTabContent) => {
  switch (activeTab) {
    case TAG_TABS.USER_TAGS:
      return <ManageTags key={TAG_TABS.USER_TAGS} usage="users" />;
    case TAG_TABS.SUBMISSION_TAGS:
      return <ManageTags key={TAG_TABS.SUBMISSION_TAGS} usage="submissions" />;
    case TAG_TABS.TAG_CATEGORIES:
      return <ManageTagCategories key={TAG_TABS.TAG_CATEGORIES} />;
    default:
      return null;
  }
};

const ManageTagTabs = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const tabs = useMemo(
    () =>
      Object.values(TAG_TABS).map(tab => ({
        label: tab,
        content: renderTabContent({ activeTab: tab })
      })),
    []
  );

  const chakraTabsProps = useMemo(
    () =>
      ({
        index: activeTabIndex,
        onChange: (index: number) => setActiveTabIndex(index)
      }) as ChakraTabsProps,
    [activeTabIndex]
  );

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box padding={{ base: 2, md: 5 }} minHeight="100%">
        <Box display={{ base: 'block', md: 'none' }} marginBottom={4}>
          <Menu>
            <MenuButton
              borderWidth="0"
              borderRadius="0"
              variant="outline"
              as={Button}
              width="100%"
              justifyContent="space-between"
              textAlign="left"
              bg="white"
              border="1px solid"
              borderColor="neutralPrimaryBorder"
              rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
              _hover={{ bg: 'white' }}
              _active={{ bg: 'white' }}
            >
              {tabs[activeTabIndex]?.label}
            </MenuButton>
            <MenuList width="100%">
              <MenuGroup title="Manage Tags">
                {tabs.map((tab, index) => (
                  <MenuItem
                    key={tab.label}
                    onClick={() => setActiveTabIndex(index)}
                    color={
                      activeTabIndex === index ? 'actionPrimaryFill' : undefined
                    }
                    fontWeight={
                      activeTabIndex === index ? 'semibold' : undefined
                    }
                  >
                    {tab.label}
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuList>
          </Menu>
        </Box>
        <Box display={{ base: 'none', md: 'block' }}>
          <Tabs
            chakraTabsProps={chakraTabsProps}
            tabs={tabs.map(tab => ({ label: tab.label, content: null }))}
          />
        </Box>
        {tabs[activeTabIndex]?.content}
      </Box>
      <Footer />
      <ScrollToTopButton />
    </Box>
  );
};

export default ManageTagTabs;
